"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Search, Compass, PenTool, ShieldCheck, ChartLine, ArrowRight } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  title: "Technical Audit",
  description: "Deep-dive assessment of existing digital stacks, legacy systems, and infrastructure bottlenecks.",
  icon: Search
}, {
  title: "Strategic Analysis",
  description: "Mapping organizational goals against technical capabilities to identify high-impact optimization paths.",
  icon: Compass
}, {
  title: "Infrastructure Architecture",
  description: "Designing robust, scalable digital blueprints that serve as the foundation for modern service delivery.",
  icon: PenTool
}, {
  title: "Workflow Automation",
  description: "Implementing precise automation layers to eliminate friction and streamline internal digital operations.",
  icon: ShieldCheck
}, {
  title: "Strategic Oversight",
  description: "Continuous monitoring and high-level advisory to maintain peak technological and organizational efficiency.",
  icon: ChartLine
}];
export default function ServiceProcess() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: timelineRef,
    isVisible: isTimelineVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [progressValue, setProgressValue] = useState(0);
  useEffect(() => {
    if (isTimelineVisible) {
      const timer = setTimeout(() => setProgressValue(100), 300);
      return () => clearTimeout(timer);
    }
  }, [isTimelineVisible]);
  return <section data-section-id="101320" id="core-framework" className="dark bg-background py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mb-20 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-sm font-mono text-primary tracking-widest mb-4 uppercase">
            Optimization Methodology
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6">
            The Optimization Framework
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            A precision-engineered methodology designed to transform organizational infrastructure through technical oversight and strategic business advisory.
          </p>
        </div>

        {}
        <div ref={timelineRef} className={`relative transition-all duration-1000 delay-200 ease-out ${isTimelineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="hidden lg:block absolute top-8 left-[10%] w-[80%] z-0">
            <Progress value={progressValue} className="h-[1px] bg-border [&>div]:bg-primary" />
          </div>

          {}
          <div className="block lg:hidden absolute top-8 bottom-8 left-8 w-[1px] bg-border z-0">
             <div className="w-full bg-primary transition-all duration-1000 ease-out" style={{
            height: `${progressValue}%`
          }} />
          </div>

          {}
          <div className="flex flex-col lg:flex-row justify-between gap-12 lg:gap-6 relative z-10">
            {steps.map((step, i) => <div key={step.title} data-index={i} className="flex flex-row lg:flex-col items-start lg:items-center text-left lg:text-center flex-1 group">
                {}
                <div className="relative flex-shrink-0 mr-6 lg:mr-0 lg:mb-8">
                  <div className="w-16 h-16 rounded-sm bg-card border border-border group-hover:border-primary transition-all duration-300 flex items-center justify-center relative z-10 shadow-sm">
                    <step.icon className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors duration-300" />
                  </div>
                  {}
                  <div className="absolute inset-0 rounded-sm bg-primary/10 scale-100 group-hover:scale-125 opacity-0 group-hover:opacity-100 transition-all duration-500 -z-10 blur-md" />
                </div>

                {}
                <div className="flex-1 pt-2 lg:pt-0">
                  <div className="text-xs font-mono text-primary/60 mb-2 lg:mb-3">
                    STAGE 
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3 tracking-tight">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-sm md:text-base">
                    {step.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {}
        <div className="mt-24 text-center lg:text-left flex flex-col lg:flex-row items-center justify-between gap-8 border-t border-border pt-12">
          <div>
            <h4 className="text-2xl font-bold text-foreground mb-2">Ready to optimize your infrastructure?</h4>
            <p className="text-muted-foreground">Engage Century Pointe Holdings for high-level consulting and technical oversight.</p>
          </div>
          <Button asChild size="lg" className="group bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(0,255,255,0.2)] rounded-sm">
            <Link to="/StrategicInquiriesPage">
              Begin Strategic Inquiry
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}